import * as Sentry from "@sentry/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { addDays, differenceInCalendarDays, format, parseISO } from "date-fns";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Header } from "../components/Header";
import { JourneyNotAvailable } from "../components/journey/JourneyNotAvailable";
import { useGlobalLoading } from "../components/loading/global-loading-context";
import { AddEventSheet } from "../components/track/AddEvent";
import { ComingUpFeed } from "../components/track/ComingUpFeed";
import { CurrentStepCard } from "../components/track/CurrentStepCard";
import { EventDetailSheet } from "../components/track/EventDetailSheet";
import { JourneyTaskDetailSheet } from "../components/track/JourneyTaskDetailSheet/JourneyTaskDetailSheet";
import { TrackOverview } from "../components/track/TrackOverview";
import { TrackSkeleton } from "../components/track/TrackSkeleton";
import { FullPageErrorState } from "../components/ui/FullPageErrorState";
import { selectCustomTaskOccurrences } from "../domain/track/customTaskOccurrenceAdapter";
import {
	type JourneyTaskOccurrence,
	selectJourneyTaskOccurrences,
} from "../domain/track/journeyTaskOccurrenceAdapter";
import { useToday } from "../hooks/track/useToday";
import { useTrackEvents } from "../hooks/track/useTrackEvents";
import { useTwoWeekWindow } from "../hooks/track/useTwoWeekWindow";
import { useJourneyProgress } from "../hooks/useJourneyProgress";
import { useUser } from "../hooks/useUser";
import { useUserType } from "../hooks/useUserType";
import { useAnalytics } from "../lib/analytics";
import { useClientTranslations } from "../lib/i18n/client";
import {
	TRACK_DAY_TAPPED,
	TRACK_EVENT_OPENED,
	TRACK_OCCURRENCE_CLICKED,
	TRACK_PAGE_VIEWED,
} from "../lib/track/analyticsEvents";
import { parseSyntheticEventId } from "../lib/track/parseSyntheticEventId";
import {
	getCustomTasks,
	updateCustomTask,
} from "../services/customTaskService";
import type { Event } from "../types/event";
import type { JourneyTask } from "../types/journey";
import { resolveJourneyType } from "../types/journey";
import styles from "./TrackPage.module.css";

type TrackCalendarItem = Event | JourneyTaskOccurrence;

function isEvent(item: TrackCalendarItem): item is Event {
	return item.source !== "journey";
}

export function TrackPage() {
	const { t, locale } = useClientTranslations("track");
	const { logTrack } = useAnalytics();
	const { setLoading } = useGlobalLoading();
	const { data: userData } = useUser();
	const { isD2C } = useUserType();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	// Phase 38 D-10 / D-13 — read ?day= query param (tolerant parse).
	const [searchParams, setSearchParams] = useSearchParams();
	const dayParam = searchParams.get("day");

	// D-13: tolerant parse — bad input returns undefined (silent fallback).
	const anchor = useMemo<Date | undefined>(() => {
		if (!dayParam || !/^\d{4}-\d{2}-\d{2}$/.test(dayParam)) return undefined;
		const d = parseISO(dayParam);
		return Number.isNaN(d.getTime()) ? undefined : d;
	}, [dayParam]);

	const window = useTwoWeekWindow(anchor);
	const eventsQuery = useTrackEvents(window.from, window.to);
	const { today } = useToday();

	// M011/S04 T04 — resolve journeyType from user profile (mirror GetReadyPage).
	// Derives role/status journeys (e.g. surrogate → surrogacy_surrogate) that
	// skip the journey-types screen and leave fertilityJourneyTypes empty.
	const journeyType = useMemo(
		() => resolveJourneyType(userData?.details),
		[userData],
	);

	const {
		journey,
		journeyStartedAt,
		stepStates,
		completedTaskIds,
		isLoading: isJourneyLoading,
		mutations,
	} = useJourneyProgress(journeyType, { locale });

	// M011/S04 T04 — derive journey-task occurrences for the calendar/Coming Up.
	// `today` from useToday() is the wall-clock anchor: it updates on
	// visibility-change and at next midnight, so the memo re-derives when the
	// day rolls over instead of going stale while the page stays mounted.
	const journeyTaskOccurrences = useMemo<JourneyTaskOccurrence[]>(() => {
		if (!journey) return [];
		return selectJourneyTaskOccurrences({
			journey,
			stepStates,
			completedTaskIds,
			journeyStartedAt,
			now: today,
		});
	}, [journey, stepStates, completedTaskIds, journeyStartedAt, today]);

	// Bug 86exzfvz4 — custom tasks with a deadline must appear on the calendar.
	// Non-blocking: failures are silently swallowed (retry: false) so a custom-
	// task API error never breaks the Track page for users who have no tasks.
	const { data: rawCustomTasks = [] } = useQuery({
		queryKey: ["customTasks"],
		queryFn: getCustomTasks,
		retry: false,
		throwOnError: false,
	});

	const customTaskOccurrences = useMemo<JourneyTaskOccurrence[]>(
		() => selectCustomTaskOccurrences(rawCustomTasks, locale),
		[rawCustomTasks, locale],
	);

	// Merge journey occurrences and dated custom-task occurrences into a single
	// list. Journey occurrences come first so they sort/display stably when two
	// items share the same deadline day.
	//
	// D2C: journey tasks are disconnected from Track — their dates aren't anchored
	// to a clinic timeline and they aren't editable here — so only custom tasks
	// (created via this page's + button) appear. B2B2C keeps journey tasks, where
	// a clinic defines the timeline.
	const journeyOccurrences = useMemo<JourneyTaskOccurrence[]>(
		() =>
			isD2C
				? customTaskOccurrences
				: [...journeyTaskOccurrences, ...customTaskOccurrences],
		[isD2C, journeyTaskOccurrences, customTaskOccurrences],
	);

	// M011/S04 T04 — first in-progress step drives CurrentStepCard.
	const inProgressJourneyStep = useMemo(() => {
		if (!journey) return null;
		for (const step of journey.steps) {
			if (stepStates.get(step.id)?.status === "in_progress") return step;
		}
		return null;
	}, [journey, stepStates]);

	// M011/S06 T09 — explicit empty-state when the current journey step has no
	// occurrences in the visible window. `deriveTaskDateWindow` always yields a
	// date window for any occurrence we surface, so "no occurrences" is the real
	// signal (not "no dated occurrences"). Without this banner, the Urgent tab
	// reads as broken.
	const currentStepHasNoOccurrences =
		inProgressJourneyStep !== null &&
		!journeyOccurrences.some((o) => o.stepId === inProgressJourneyStep.id);

	// M011/S04 T04 — non-IUI (or any unsupported) journey → shared empty state.
	// Mirrors GetReadyPage: only show after both progress + events queries have
	// finished loading and we have no journey JSON to render from.
	//
	// D2C never shows this — Track there is purely events + custom tasks, so it
	// must not gate the calendar on a resolved journey (which would otherwise hide
	// the user's custom tasks).
	const showJourneyNotAvailable =
		!isD2C && !isJourneyLoading && !eventsQuery.isLoading && journey === null;

	// Show the global loader during inter-month refetches (prev/next paging).
	// Initial load is covered by <TrackSkeleton /> so we exclude isLoading.
	useEffect(() => {
		const refetching = eventsQuery.isFetching && !eventsQuery.isLoading;
		setLoading(refetching);
		return () => setLoading(false);
	}, [eventsQuery.isFetching, eventsQuery.isLoading, setLoading]);

	const [sheetDayItems, setSheetDayItems] = useState<TrackCalendarItem[]>([]);
	const [sheetIndex, setSheetIndex] = useState(0);

	// 86ey0mc8v — active-day offset (from today) that the Coming-Up feed shows.
	// A calendar day-tap sets this so the feed fills with that day's items; the
	// feed's own ‹ › stepper updates it too (controlled day-stepper).
	const [comingUpDayOffset, setComingUpDayOffset] = useState(0);

	// The calendar's highlight border follows the active day (today + offset),
	// so it moves to whichever day is selected via a calendar tap or the
	// Coming-Up feed's ‹ › stepper.
	const selectedDayIso = format(
		addDays(today, comingUpDayOffset),
		"yyyy-MM-dd",
	);

	// Derived: which sheet is open based on the active item type
	const activeSheetItem = sheetDayItems[sheetIndex] ?? null;
	const sheetOpen = activeSheetItem !== null && isEvent(activeSheetItem);
	const journeyTaskSheetOpen =
		activeSheetItem !== null && !isEvent(activeSheetItem);

	// EventDetailSheet needs Event[] filtered from the mixed list
	const sheetDayEvents = sheetDayItems.filter(isEvent);
	// EventDetailSheet activeIndex is the position within events-only sublist
	const sheetEventIndex = sheetDayEvents.indexOf(activeSheetItem as Event);

	const journeyTaskSheetTask: JourneyTask | null =
		activeSheetItem !== null && !isEvent(activeSheetItem)
			? activeSheetItem.task
			: null;

	// Phase 37 D-15 / D-16 — TrackPage owns AddEventSheet open state.
	// editingEvent === null → create mode (chooser-first); non-null → edit mode (D-18).
	const [isAddEventOpen, setIsAddEventOpen] = useState(false);
	const [editingEvent, setEditingEvent] = useState<Event | null>(null);
	// Day context captured when entering edit, so the edit-mode Back button
	// (Figma 3616-13417) can reopen EventDetailSheet on the same event.
	const [editReturnContext, setEditReturnContext] = useState<{
		items: TrackCalendarItem[];
		index: number;
	} | null>(null);

	// INTG-03: track_page_viewed once on mount (CLAUDE.md useEffect ref pattern)
	const hasTrackedPageView = useRef(false);
	useEffect(() => {
		if (!hasTrackedPageView.current) {
			hasTrackedPageView.current = true;
			logTrack(TRACK_PAGE_VIEWED);
		}
	}, [logTrack]);

	// Phase 38 D-12 — pulse target on deep-link land. Null = no pulse.
	const [focusDayIso, setFocusDayIso] = useState<string | null>(null);

	// D-13 + D-12 — consume ?day= on every appearance (notification click can fire
	// multiple times in the same TrackPage mount; each click should re-anchor and
	// pulse). The effect's `[dayParam, anchor, setSearchParams]` deps already gate
	// it correctly: it fires each time dayParam transitions from null → present,
	// and after the setSearchParams clear it goes back to null without re-firing
	// until the next deep-link arrives. React Strict Mode's dev-only double-invoke
	// is idempotent here (clearing already-empty params is a no-op; setting
	// focusDayIso to the same value is a no-op).
	useEffect(() => {
		if (!dayParam) return;
		if (anchor) {
			setFocusDayIso(dayParam);
		} else {
			// Bad ?day — silent fallback + observability breadcrumb (D-13).
			// Date strings are NOT in PHI denylist (RESEARCH Q6).
			Sentry.addBreadcrumb({
				category: "navigation",
				message: "Invalid ?day= param on /track",
				level: "info",
				data: { rawDay: dayParam },
			});
		}
		// Clear ONLY the `day` param so refresh does NOT re-anchor (D-10 / D-11
		// / Specifics §6). Preserves any other query params /track may carry now
		// or in the future (analytics tracking, A/B test buckets, referral
		// sources, etc.). PR review (Copilot, Phase 38 PR #103) caught that
		// `setSearchParams({}, ...)` would clobber unrelated params too.
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev);
				next.delete("day");
				return next;
			},
			{ replace: true },
		);
	}, [dayParam, anchor, setSearchParams]);

	// Auto-clear pulse after ~1.6s so a re-render doesn't replay it (D-12).
	useEffect(() => {
		if (focusDayIso === null) return;
		const t = setTimeout(() => setFocusDayIso(null), 1600);
		return () => clearTimeout(t);
	}, [focusDayIso]);

	const fireEventOpened = useCallback(
		(event: Event) => {
			// WR-07: enums-only payload — eventId removed to align with the
			// TRACK_EVENT_CREATED|EDITED|DELETED contract (Phase 37 PHI invariant).
			const synthetic = parseSyntheticEventId(event.id);
			logTrack(TRACK_EVENT_OPENED, {
				type: event.type,
				isRecurring: synthetic !== null,
			});
		},
		[logTrack],
	);

	// Build the full day's items (events + journey/custom occurrences) sorted by
	// time, so the detail sheet's ‹ › can page through everything on that day —
	// not just the single tapped card. Mirrors ComingUpFeed's sort.
	const buildDayItems = useCallback(
		(dayIso: string): TrackCalendarItem[] => {
			const sortKey = (item: TrackCalendarItem) =>
				isEvent(item) ? item.dateTime : item.startIso;
			const dayEvents = (eventsQuery.data ?? []).filter(
				(e) => e.dateTime.slice(0, 10) === dayIso,
			);
			const dayOccurrences = journeyOccurrences.filter(
				(o) => o.startIso.slice(0, 10) === dayIso,
			);
			return [...dayEvents, ...dayOccurrences].sort((a, b) =>
				sortKey(a).localeCompare(sortKey(b)),
			);
		},
		[eventsQuery.data, journeyOccurrences],
	);

	const handleJourneyOccurrenceClick = useCallback(
		(occurrence: JourneyTaskOccurrence) => {
			logTrack(TRACK_OCCURRENCE_CLICKED, {
				taskId: occurrence.taskId,
				stepId: occurrence.stepId,
			});
			const items = buildDayItems(occurrence.startIso.slice(0, 10));
			const idx = items.findIndex((i) => !isEvent(i) && i.id === occurrence.id);
			setSheetDayItems(items.length > 0 ? items : [occurrence]);
			setSheetIndex(Math.max(0, idx));
		},
		[logTrack, buildDayItems],
	);

	const handleDayTap = useCallback(
		(dayIso: string, itemsForDay: TrackCalendarItem[]) => {
			const eventsForDay = itemsForDay.filter(isEvent);
			logTrack(TRACK_DAY_TAPPED, {
				dayIso,
				eventCount: eventsForDay.length,
			});
			// 86ey0mc8v — a calendar day-tap drives the Coming-Up feed to that day
			// (so the list fills with that day's items) and pulses the calendar
			// cell. It no longer opens a detail sheet; only tapping a card in the
			// Coming-Up list opens a modal.
			const parsed = parseISO(dayIso);
			if (!Number.isNaN(parsed.getTime())) {
				setComingUpDayOffset(differenceInCalendarDays(parsed, today));
			}
			setFocusDayIso(dayIso);
		},
		[logTrack, today],
	);

	const handleComingUpClick = useCallback(
		(event: Event) => {
			const items = buildDayItems(event.dateTime.slice(0, 10));
			const idx = items.findIndex((i) => isEvent(i) && i.id === event.id);
			setSheetDayItems(items.length > 0 ? items : [event]);
			setSheetIndex(Math.max(0, idx));
			fireEventOpened(event);
		},
		[buildDayItems, fireEventOpened],
	);

	const handleSheetIndexChange = useCallback(
		(newIndex: number) => {
			setSheetIndex(newIndex);
			const target = sheetDayItems[newIndex];
			if (target && isEvent(target)) fireEventOpened(target);
		},
		[sheetDayItems, fireEventOpened],
	);

	const handleSheetClose = useCallback(() => setSheetDayItems([]), []);

	// D2C: event completion has no era-api endpoint yet — UI-only toast + close.
	// TODO(era-api): persist event completion, then replace this with a mutation.
	const handleMarkEventComplete = useCallback(() => {
		toast.success(t("event.detail.markCompletedToast"));
		handleSheetClose();
	}, [t, handleSheetClose]);

	// D2C: completing an occurrence persists, then closes the sheet. Custom-task
	// occurrences (id prefixed "custom:" by selectCustomTaskOccurrences) are NOT
	// journey-progress rows — they complete via the custom-task endpoint, so
	// route those there; everything else uses journey-progress (same as Journey).
	const handleMarkJourneyTaskComplete = useCallback(() => {
		const taskId = journeyTaskSheetTask?.id;
		if (!taskId) return;

		const isCustomOccurrence =
			activeSheetItem !== null &&
			!isEvent(activeSheetItem) &&
			activeSheetItem.id.startsWith("custom:");

		if (isCustomOccurrence) {
			updateCustomTask(taskId, { status: "completed" })
				.then(() => {
					toast.success(t("event.detail.markCompletedToast"));
					return queryClient.invalidateQueries({ queryKey: ["customTasks"] });
				})
				.catch(() => toast.error(t("event.detail.markCompleteError")));
			handleSheetClose();
			return;
		}

		mutations.completeTask.mutate(
			{ taskId, progressPercent: 100 },
			{
				onSuccess: () => toast.success(t("event.detail.markCompletedToast")),
				onError: () => toast.error(t("event.detail.markCompleteError")),
			},
		);
		handleSheetClose();
	}, [
		journeyTaskSheetTask,
		activeSheetItem,
		mutations.completeTask,
		queryClient,
		t,
		handleSheetClose,
	]);

	// Phase 37 ADD-01 / D-16 — (+) buttons in TrackOverview + ComingUpFeed
	// open AddEventSheet in create mode (chooser first).
	const handleAddEventOpen = useCallback(() => {
		setEditingEvent(null);
		setIsAddEventOpen(true);
	}, []);

	// Phase 37 EDM-04 / D-18 — Edit click in EventDetailSheet closes the detail
	// sheet and opens AddEventSheet in edit mode (skips chooser, pre-fills form).
	// All three setters run in the same React batch — single render, no flicker
	// (T-37-EDIT-FLOW-RACE mitigation).
	const handleEditFromDetail = useCallback(
		(event: Event) => {
			setEditReturnContext({ items: sheetDayItems, index: sheetIndex });
			setSheetDayItems([]);
			setEditingEvent(event);
			setIsAddEventOpen(true);
		},
		[sheetDayItems, sheetIndex],
	);

	// Edit-mode Back (Figma 3616-13417) — closes AddEventSheet and reopens
	// EventDetailSheet on the same event by restoring the day context captured
	// at edit entry. No save occurred, so the cached items are still current.
	const handleEditBack = useCallback(() => {
		setIsAddEventOpen(false);
		setEditingEvent(null);
		if (editReturnContext) {
			setSheetDayItems(editReturnContext.items);
			setSheetIndex(editReturnContext.index);
			setEditReturnContext(null);
		}
	}, [editReturnContext]);

	// Phase 37 D-15 — closing AddEventSheet (Cancel, Save success, X) clears
	// editingEvent so the next open defaults back to create mode.
	const handleAddEventClose = useCallback(() => {
		setIsAddEventOpen(false);
		setEditingEvent(null);
		setEditReturnContext(null);
	}, []);

	return (
		<div className={styles.wrapper}>
			<Header />
			<main className={styles.main}>
				{eventsQuery.isLoading && <TrackSkeleton />}
				{eventsQuery.isError && (
					<FullPageErrorState
						titleKey="error.title"
						descriptionKey="error.description"
						retryKey="error.retry"
						onRetry={() => eventsQuery.refetch()}
						t={t}
					/>
				)}
				{!eventsQuery.isLoading &&
					!eventsQuery.isError &&
					showJourneyNotAvailable && <JourneyNotAvailable />}
				{!eventsQuery.isLoading &&
					!eventsQuery.isError &&
					!showJourneyNotAvailable && (
						<>
							{/* D2C: journey is disconnected from Track, so no current-step
							    card or journey empty-state — only events + custom tasks. */}
							{!isD2C && (
								<CurrentStepCard journeyStep={inProgressJourneyStep} />
							)}
							{!isD2C && currentStepHasNoOccurrences && (
								<section
									className={styles.datelessEmpty}
									data-testid="track-dateless-empty"
								>
									<p className={styles.datelessTitle}>{t("dateless.title")}</p>
									<p className={styles.datelessBody}>{t("dateless.body")}</p>
									<button
										type="button"
										className={styles.datelessCta}
										onClick={() => navigate("/get-ready")}
									>
										{t("dateless.cta")}
									</button>
								</section>
							)}
							<TrackOverview
								events={eventsQuery.data}
								journeyOccurrences={journeyOccurrences}
								window={window}
								onDayTap={handleDayTap}
								onAddEvent={handleAddEventOpen}
								focusDayIso={focusDayIso}
								selectedDayIso={selectedDayIso}
							/>
							<ComingUpFeed
								events={eventsQuery.data}
								journeyOccurrences={journeyOccurrences}
								isLoading={false}
								onEventClick={handleComingUpClick}
								onJourneyOccurrenceClick={handleJourneyOccurrenceClick}
								onAddEvent={handleAddEventOpen}
								dayOffset={comingUpDayOffset}
								onDayOffsetChange={setComingUpDayOffset}
							/>
						</>
					)}
			</main>
			<EventDetailSheet
				isOpen={sheetOpen}
				onClose={handleSheetClose}
				events={sheetDayEvents}
				activeIndex={Math.max(0, sheetEventIndex)}
				onIndexChange={(evtIdx) => {
					const target = sheetDayEvents[evtIdx];
					if (!target) return;
					const mixedIdx = sheetDayItems.indexOf(target);
					handleSheetIndexChange(mixedIdx >= 0 ? mixedIdx : evtIdx);
				}}
				onEdit={handleEditFromDetail}
				onMarkComplete={handleMarkEventComplete}
				canPrevMixed={sheetIndex > 0}
				canNextMixed={sheetIndex < sheetDayItems.length - 1}
				onPrevMixed={() => handleSheetIndexChange(sheetIndex - 1)}
				onNextMixed={() => handleSheetIndexChange(sheetIndex + 1)}
			/>
			<JourneyTaskDetailSheet
				isOpen={journeyTaskSheetOpen}
				onClose={handleSheetClose}
				task={journeyTaskSheetTask}
				canPrev={sheetIndex > 0}
				canNext={sheetIndex < sheetDayItems.length - 1}
				onPrev={() => handleSheetIndexChange(sheetIndex - 1)}
				onNext={() => handleSheetIndexChange(sheetIndex + 1)}
				onMarkComplete={isD2C ? handleMarkJourneyTaskComplete : undefined}
				markCompleteLabel={t("event.detail.markCompleted")}
			/>
			<AddEventSheet
				isOpen={isAddEventOpen}
				onClose={handleAddEventClose}
				initialMode={editingEvent ? "edit" : "create"}
				initialType={editingEvent?.type}
				initialValues={editingEvent ?? undefined}
				onBack={editingEvent ? handleEditBack : undefined}
			/>
		</div>
	);
}
